import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getReviewToken, submitReview } from "@/lib/api";

export default function ReviewSubmit() {
  const { token } = useParams();
  const { lang } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setLoading(true);
    getReviewToken(token)
      .then((d) => {
        setInfo(d);
        setName(d.customer_name || "");
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail;
        setLoadError(
          msg ||
            (lang === "en"
              ? "This review link is invalid or has expired."
              : "Denne anmeldelseslenken er ugyldig eller utløpt.")
        );
      })
      .finally(() => setLoading(false));
  }, [token, lang]);

  const submit = async (e) => {
    e.preventDefault();
    if (!stars || !name.trim()) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      await submitReview(token, { customer_name: name.trim(), stars, comment });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setSubmitError(
        msg ||
          (lang === "en"
            ? "Could not submit your review. Please try again."
            : "Kunne ikke sende inn anmeldelsen. Prøv igjen.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#6B726C]">
        {lang === "en" ? "Loading…" : "Laster…"}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <AlertCircle className="w-10 h-10 text-[#D97B66] mx-auto mb-4" strokeWidth={1.4} />
        <h1 className="font-heading text-2xl text-[#2C302E] font-light mb-3">
          {lang === "en" ? "Sorry…" : "Beklager…"}
        </h1>
        <p className="text-[#6B726C]">{loadError}</p>
        <Link
          to="/butikk"
          className="mt-8 inline-flex items-center gap-2 text-sm text-[#D97B66] hover:text-[#C66A55]"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "en" ? "Back to shop" : "Tilbake til butikk"}
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="review-thanks">
        <CheckCircle2
          className="w-12 h-12 text-[#8BA896] mx-auto mb-5"
          strokeWidth={1.3}
        />
        <h1 className="font-heading text-3xl text-[#2C302E] font-light mb-3">
          {lang === "en"
            ? "Thank you for your review!"
            : "Tusen takk for anmeldelsen!"}
        </h1>
        <p className="text-[#6B726C] leading-relaxed">
          {lang === "en"
            ? "Your feedback means a lot to us. The review will appear on the product page once approved."
            : "Tilbakemeldingen din betyr enormt mye for oss. Anmeldelsen vises på produktsiden så snart den er godkjent."}
        </p>
        <Link
          to={`/produkt/${info.product_slug}`}
          className="mt-8 inline-block bg-[#2C302E] hover:bg-[#1f221f] text-white rounded-full px-7 py-3 font-medium"
        >
          {lang === "en" ? "View product" : "Se produktet"}
        </Link>
      </div>
    );
  }

  const product = info.product;
  const title = lang === "en"
    ? product?.title_en || info.product_title_no
    : product?.title_no || info.product_title_no;

  return (
    <div className="bg-[#F9F8F6] min-h-screen py-12 md:py-20">
      <div className="max-w-2xl mx-auto px-6">
        <Link
          to={`/produkt/${info.product_slug}`}
          className="inline-flex items-center gap-2 text-sm text-[#6B726C] hover:text-[#2C302E] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "en" ? "Back to product" : "Tilbake til produktet"}
        </Link>

        <div className="bg-white border border-[#E5E2DC] rounded-3xl p-6 md:p-10">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D97B66] font-medium mb-3">
            {lang === "en" ? "Write a review" : "Skriv en anmeldelse"}
          </p>
          <h1 className="font-heading text-3xl text-[#2C302E] font-light leading-tight">
            {title}
          </h1>

          {product?.image_url && (
            <div className="mt-6 mb-8 aspect-[16/9] rounded-2xl overflow-hidden border border-[#E5E2DC]">
              <img
                src={product.image_url}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            {/* Stars */}
            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                {lang === "en" ? "Your rating *" : "Din vurdering *"}
              </label>
              <div
                className="mt-3 flex items-center gap-1"
                data-testid="review-stars"
                onMouseLeave={() => setHoverStars(0)}
              >
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hoverStars || stars) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onMouseEnter={() => setHoverStars(n)}
                      onClick={() => setStars(n)}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    >
                      <Star
                        className="w-9 h-9"
                        fill={active ? "#E8B547" : "transparent"}
                        stroke={active ? "#E8B547" : "#C7C3BC"}
                        strokeWidth={1.5}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                {lang === "en" ? "Your name *" : "Navnet ditt *"}
              </label>
              <input
                type="text"
                required
                maxLength={80}
                data-testid="review-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang === "en" ? "How should we display your name?" : "Hvordan skal navnet ditt vises?"}
                className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-3 text-[#2C302E] placeholder:text-[#6B726C]/50 focus:outline-none focus:border-[#8BA896]"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-[#6B726C] font-medium">
                {lang === "en" ? "Comment (optional)" : "Kommentar (valgfritt)"}
              </label>
              <textarea
                rows={5}
                maxLength={1000}
                data-testid="review-comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  lang === "en"
                    ? "Tell other parents what you liked about this product…"
                    : "Fortell andre foreldre hva du likte med dette produktet…"
                }
                className="mt-2 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl px-4 py-3 text-[#2C302E] placeholder:text-[#6B726C]/50 focus:outline-none focus:border-[#8BA896] resize-none"
              />
              <p className="text-xs text-[#6B726C] mt-1">
                {comment.length}/1000
              </p>
            </div>

            {submitError && (
              <p className="text-sm text-[#D97B66]" data-testid="review-error">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              data-testid="review-submit-btn"
              disabled={submitting || !stars || !name.trim()}
              className="w-full bg-[#D97B66] hover:bg-[#C66A55] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-8 py-4 font-medium transition-colors"
            >
              {submitting
                ? lang === "en"
                  ? "Sending…"
                  : "Sender…"
                : lang === "en"
                ? "Submit review"
                : "Send anmeldelse"}
            </button>

            <p className="text-xs text-[#6B726C] text-center">
              {lang === "en"
                ? "Your review will be reviewed before it appears publicly."
                : "Anmeldelsen din vurderes før den vises offentlig."}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
